import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useClassroom } from '../contexts/ClassroomContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { BookOpen, MapPin, Check, ChevronDown, BarChart3, GraduationCap, Download, X, Calendar, Loader2 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STRAND_COLORS = {
  Listening: '#4C6EF5',
  Speaking: '#E03131',
  Reading: '#2F9E44',
  Writing: '#F59F00',
};

const GRADE_COLORS = {
  'Grade 5': '#7C3AED',
  'Grade 6': '#0891B2',
  'Grade 7': '#C2410C',
};

const CEFR_COLORS = {
  'PRE-A1': '#2F9E44',
  'A1': '#4C6EF5',
  'A1-A2': '#E8590C',
  'A2': '#E03131',
};

// --- Shared components ---

function DepthBadge({ itemCount, catalogueTotal }) {
  const { t } = useLanguage();
  if (!catalogueTotal || catalogueTotal === 0) return null;
  const pct = Math.round((itemCount / catalogueTotal) * 100);
  let labelKey, bg, fg;
  if (pct >= 25) {
    labelKey = 'depth.strong'; bg = '#ECFDF5'; fg = '#059669';
  } else if (pct >= 5) {
    labelKey = 'depth.developing'; bg = '#FEF9C3'; fg = '#A16207';
  } else {
    labelKey = 'depth.introduced'; bg = '#FEF2F2'; fg = '#DC2626';
  }
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
      padding: '0.125rem 0.5rem', borderRadius: '0.25rem',
      background: bg, fontFamily: 'Nunito, sans-serif', fontSize: '0.7rem',
      fontWeight: 700, color: fg, letterSpacing: '0.02em',
    }}>
      {t(labelKey)}
    </span>
  );
}

// --- Curriculum Tab Components ---

function ExpectationRow({ exp, color }) {
  const { t } = useLanguage();
  const gamesStr = exp.games && exp.games.length > 0
    ? exp.games.map(g => g.name).join(', ')
    : '';
  const lastDate = exp.last_practiced ? new Date(exp.last_practiced) : null;
  const daysSince = lastDate ? Math.floor((Date.now() - lastDate.getTime()) / 86400000) : null;
  const lastLabel = daysSince === 0 ? t('prog.today') : daysSince === 1 ? '1d' : daysSince !== null ? `${daysSince}d` : '';
  const catTotal = exp.catalogue_total || 0;
  const depthPct = catTotal > 0 ? Math.round((exp.item_count / catTotal) * 100) : 0;

  return (
    <div
      data-testid={`curriculum-row-${exp.code}`}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
        padding: '0.75rem 1rem', borderRadius: '0.5rem',
        background: exp.practiced ? `${color}08` : '#FAFBFC',
        border: `1.5px solid ${exp.practiced ? `${color}30` : '#F1F5F9'}`,
      }}
    >
      <div style={{
        width: 24, height: 24, borderRadius: '50%', flexShrink: 0, marginTop: 1,
        background: exp.practiced ? color : '#E2E8F0',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {exp.practiced && <Check size={13} color="white" strokeWidth={3} />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.375rem', flexWrap: 'wrap' }}>
          <span style={{
            fontFamily: 'Nunito, sans-serif', fontSize: '0.9rem', fontWeight: 700,
            color: exp.practiced ? '#1A1D20' : '#94A3B8',
          }}>
            {exp.title || exp.label || exp.code}
          </span>
          {exp.code && exp.code !== (exp.title || '') && (
            <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.725rem', fontWeight: 500, color: '#B0B8C4' }}>
              {exp.code}
            </span>
          )}
          {exp.practiced && catTotal > 0 && (
            <DepthBadge itemCount={exp.item_count} catalogueTotal={catTotal} />
          )}
        </div>
        {exp.text && (
          <div style={{
            fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem',
            color: exp.practiced ? '#475569' : '#94A3B8',
            marginTop: '0.25rem', lineHeight: 1.45,
          }}>
            {exp.text}
          </div>
        )}
        {exp.practiced ? (
          <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', color: '#64748B', marginTop: '0.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.2rem' }}>
              <span style={{ fontWeight: 600, color: '#1A1D20' }}>
                {exp.item_count} {t('dash.of')} {catTotal} {t('page.items')}
              </span>
              {catTotal > 0 && (
                <>
                  <div style={{ flex: '0 0 60px', height: 4, background: '#F1F5F9', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', borderRadius: 2,
                      width: `${Math.min(depthPct, 100)}%`,
                      background: depthPct >= 25 ? '#059669' : depthPct >= 5 ? '#EAB308' : '#EF4444',
                    }} />
                  </div>
                  <span style={{ fontSize: '0.725rem', color: '#94A3B8' }}>{depthPct}%</span>
                </>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', alignItems: 'center' }}>
              <span>{exp.session_count} {exp.session_count !== 1 ? t('dash.sessions') : t('dash.session')}</span>
              {exp.game_count > 0 && <>
                <span style={{ color: '#CBD5E1' }}>&middot;</span>
                <span>{exp.game_count} {exp.game_count !== 1 ? t('dash.games') : t('dash.game')}</span>
              </>}
              {gamesStr && <>
                <span style={{ color: '#CBD5E1' }}>&middot;</span>
                <span>{gamesStr}</span>
              </>}
              {lastLabel && <>
                <span style={{ color: '#CBD5E1' }}>&middot;</span>
                <span>{lastLabel}</span>
              </>}
            </div>
          </div>
        ) : (
          <div style={{
            fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', color: '#94A3B8',
            marginTop: '0.25rem', fontStyle: 'italic',
          }}>
            {t('prog.notYetEncountered')} &middot; {catTotal > 0 ? `${catTotal} ${t('prog.itemsAvailableInLib')}` : t('prog.noItemsAvailable')}
          </div>
        )}
      </div>
    </div>
  );
}

function CurriculumSection({ title, icon: Icon, data, colorMap, groupKey, groupField }) {
  const [expandedGroup, setExpandedGroup] = useState(null);
  const { t } = useLanguage();
  if (!data || data.total === 0) return null;

  const practiced = data.practiced || 0;
  const groups = data[groupKey] || {};

  return (
    <div data-testid={`curriculum-section-${title.toLowerCase().replace(/\s/g, '-')}`} style={{ marginBottom: '2.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
        <Icon size={20} color="#1E2140" />
        <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.35rem', fontWeight: 700, color: '#1A1D20', margin: 0 }}>
          {title}
        </h2>
      </div>

      {/* Summary card — dot grid */}
      <div style={{
        background: 'white', borderRadius: '0.875rem', border: '1px solid #E2E8F0',
        padding: '1.25rem 1.5rem', marginBottom: '1.25rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.875rem' }}>
          <div>
            <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.9rem', color: '#1A1D20' }}>
              {t('prog.yourSessionsInclude')}{' '}
              <strong style={{ fontFamily: 'Fredoka, sans-serif', fontSize: '1.5rem' }}>{practiced}</strong>
              {' '}{t('dash.of')} {data.total} {groupKey === 'by_strand' ? t('prog.curriculumExpectations') : t('prog.contentAreas')}
            </span>
          </div>
          {data.session_count > 0 && (
            <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', color: '#94A3B8' }}>
              {t('dash.across')} {data.session_count} {data.session_count !== 1 ? t('dash.sessions') : t('dash.session')}
              {data.unique_games > 0 && <> &middot; {data.unique_games} {data.unique_games !== 1 ? t('dash.games') : t('dash.game')}</>}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '0.875rem' }}>
          {Object.entries(groups).map(([name, info]) => {
            const items = info[groupField] || [];
            return items.map(exp => (
              <div
                key={exp.code}
                title={`${exp.title || exp.label || exp.code}${exp.practiced ? ' (touched)' : ''}`}
                style={{
                  width: 18, height: 18, borderRadius: 4,
                  background: exp.practiced ? (colorMap[name] || '#94A3B8') : '#F1F5F9',
                  border: exp.practiced ? 'none' : '1px solid #E2E8F0',
                }}
              />
            ));
          })}
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {Object.entries(groups).map(([name, info]) => (
            <div key={name} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: colorMap[name] || '#94A3B8' }} />
              <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.825rem', fontWeight: 600, color: '#1A1D20' }}>{name}</span>
              <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.825rem', color: '#94A3B8' }}>{info.practiced}/{info.total}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Detail groups */}
      {Object.entries(groups).map(([name, info]) => {
        const color = colorMap[name] || '#94A3B8';
        const isExpanded = expandedGroup === name;
        const items = info[groupField] || [];

        return (
          <div key={name} style={{
            background: 'white', borderRadius: '0.875rem', border: '1px solid #E2E8F0',
            marginBottom: '0.75rem', overflow: 'hidden',
          }}>
            <div
              onClick={() => setExpandedGroup(isExpanded ? null : name)}
              style={{
                padding: '0.875rem 1.25rem', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: isExpanded ? '#FAFBFC' : 'white',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{ width: 10, height: 10, borderRadius: 3, background: color }} />
                <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.925rem', fontWeight: 700, color: '#1A1D20' }}>{name}</span>
                <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', color: '#94A3B8' }}>{info.practiced}/{info.total}</span>
              </div>
              <ChevronDown size={16} color="#94A3B8" style={{
                transition: 'transform 0.2s',
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
              }} />
            </div>
            {isExpanded && (
              <div style={{ padding: '0.5rem 1.25rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {items.map(exp => (
                  <ExpectationRow key={exp.code} exp={exp} color={color} />
                ))}
              </div>
            )}
          </div>
        );
      })}

      <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.775rem', color: '#94A3B8', marginTop: '0.75rem', fontStyle: 'italic', lineHeight: 1.5 }}>
        {t('prog.depthNote')}
      </p>
    </div>
  );
}

// --- 7-province (SK/MB/AB/NL/NS/NB/PEI) section ---
// Backend endpoint returns flat content_areas[]. We group by confidence (high/medium/low)
// for at-a-glance triage. Ontario + BC stay on their dedicated, richer renderers above.

const PROVINCE_ORDER = [
  'saskatchewan', 'manitoba', 'alberta',
  'newfoundland', 'nova_scotia', 'new_brunswick', 'pei',
];

const PROVINCE_LABELS = {
  saskatchewan:  { en: 'Saskatchewan',                fr: 'Saskatchewan' },
  manitoba:      { en: 'Manitoba',                    fr: 'Manitoba' },
  alberta:       { en: 'Alberta',                     fr: 'Alberta' },
  newfoundland:  { en: 'Newfoundland & Labrador',     fr: 'Terre-Neuve-et-Labrador' },
  nova_scotia:   { en: 'Nova Scotia',                 fr: 'Nouvelle-Écosse' },
  new_brunswick: { en: 'New Brunswick',               fr: 'Nouveau-Brunswick' },
  pei:           { en: 'Prince Edward Island',        fr: 'Île-du-Prince-Édouard' },
};

// Strand color palette for the 7 non-Ontario provinces. Each province has its own
// strand vocabulary (Communication Skills / Oral Communication / GCO 1 / etc.) so
// we color-cycle deterministically rather than maintain a per-strand color map.
const STRAND_PALETTE = ['#4C6EF5', '#E03131', '#2F9E44', '#F59F00', '#7C3AED', '#0891B2', '#C2410C'];
function strandColor(strand) {
  if (!strand) return '#94A3B8';
  let hash = 0;
  for (let i = 0; i < strand.length; i++) hash = (hash * 31 + strand.charCodeAt(i)) | 0;
  return STRAND_PALETTE[Math.abs(hash) % STRAND_PALETTE.length];
}

function ProvinceCurriculumSection({ provinceId, data }) {
  const { t, lang } = useLanguage();
  const [expandedGroup, setExpandedGroup] = useState(null);
  if (!data || !data.total) return null;

  const label = (PROVINCE_LABELS[provinceId] || {})[lang] || provinceId;
  const sourcePub = (data.province_meta?.source || {}).publisher;
  const sourceYear = (data.province_meta?.source || {}).year;
  const areas = data.content_areas || [];
  const practiced = data.practiced || 0;

  // Group by curriculum strand (the curriculum's own organization). Mirrors Ontario's
  // by-strand view. `strand` comes from the per-province alignment_lookup; never derived.
  const buckets = {};
  areas.forEach(a => {
    const s = a.strand || (lang === 'fr' ? 'Autre' : 'Other');
    if (!buckets[s]) buckets[s] = [];
    buckets[s].push(a);
  });
  const orderedStrands = Object.keys(buckets).sort();

  return (
    <div data-testid={`curriculum-section-${provinceId}`} style={{ marginBottom: '2.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
        <MapPin size={20} color="#1E2140" />
        <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.35rem', fontWeight: 700, color: '#1A1D20', margin: 0 }}>
          {label}
        </h2>
      </div>
      {sourcePub && (
        <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.75rem', color: '#94A3B8', margin: '0 0 1rem 28px', fontStyle: 'italic' }}>
          {sourcePub}{sourceYear ? ` · ${sourceYear}` : ''}
        </p>
      )}

      {/* Summary card — dot grid colored by strand */}
      <div style={{
        background: 'white', borderRadius: '0.875rem', border: '1px solid #E2E8F0',
        padding: '1.25rem 1.5rem', marginBottom: '1.25rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.875rem' }}>
          <div>
            <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.9rem', color: '#1A1D20' }}>
              {t('prog.yourSessionsInclude')}{' '}
              <strong style={{ fontFamily: 'Fredoka, sans-serif', fontSize: '1.5rem' }}>{practiced}</strong>
              {' '}{t('dash.of')} {data.total} {t('prog.contentAreas')}
            </span>
          </div>
          {data.session_count > 0 && (
            <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', color: '#94A3B8' }}>
              {t('dash.across')} {data.session_count} {data.session_count !== 1 ? t('dash.sessions') : t('dash.session')}
              {data.unique_games > 0 && <> &middot; {data.unique_games} {data.unique_games !== 1 ? t('dash.games') : t('dash.game')}</>}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '0.875rem' }}>
          {areas.map(a => (
            <div
              key={a.code}
              title={`${a.title || a.label || a.code}${a.practiced ? ' (touched)' : ''}`}
              style={{
                width: 18, height: 18, borderRadius: 4,
                background: a.practiced ? strandColor(a.strand) : '#F1F5F9',
                border: a.practiced ? 'none' : '1px solid #E2E8F0',
              }}
            />
          ))}
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {orderedStrands.map(strand => {
            const total = buckets[strand].length;
            const practicedCount = buckets[strand].filter(a => a.practiced).length;
            return (
              <div key={strand} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: strandColor(strand) }} />
                <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.825rem', fontWeight: 600, color: '#1A1D20' }}>
                  {strand}
                </span>
                <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.825rem', color: '#94A3B8' }}>
                  {practicedCount}/{total}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Per-strand collapsible groups */}
      {orderedStrands.map(strand => {
        const color = strandColor(strand);
        const isExpanded = expandedGroup === strand;
        const items = buckets[strand];
        const practicedCount = items.filter(a => a.practiced).length;

        return (
          <div key={strand} style={{
            background: 'white', borderRadius: '0.875rem', border: '1px solid #E2E8F0',
            marginBottom: '0.75rem', overflow: 'hidden',
          }}>
            <div
              data-testid={`province-${provinceId}-strand-${strand.replace(/\s+/g, '-').toLowerCase()}`}
              onClick={() => setExpandedGroup(isExpanded ? null : strand)}
              style={{
                padding: '0.875rem 1.25rem', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: isExpanded ? '#FAFBFC' : 'white',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{ width: 10, height: 10, borderRadius: 3, background: color }} />
                <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.925rem', fontWeight: 700, color: '#1A1D20' }}>
                  {strand}
                </span>
                <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', color: '#94A3B8' }}>
                  {practicedCount}/{items.length}
                </span>
              </div>
              <ChevronDown size={16} color="#94A3B8" style={{
                transition: 'transform 0.2s',
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
              }} />
            </div>
            {isExpanded && (
              <div style={{ padding: '0.5rem 1.25rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {items.map(a => (
                  <ExpectationRow key={a.code} exp={a} color={color} />
                ))}
              </div>
            )}
          </div>
        );
      })}

      <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.775rem', color: '#94A3B8', marginTop: '0.75rem', fontStyle: 'italic', lineHeight: 1.5 }}>
        {t('prog.depthNote')}
      </p>
    </div>
  );
}

function CurriculumTab({ ontario, bc, provinces }) {
  const { t } = useLanguage();
  return (
    <>
      <CurriculumSection
        title={t('prog.ontarioCurriculum')}
        icon={BookOpen}
        data={ontario}
        colorMap={STRAND_COLORS}
        groupKey="by_strand"
        groupField="expectations"
      />
      <CurriculumSection
        title={t('prog.bcCurriculum')}
        icon={MapPin}
        data={bc}
        colorMap={GRADE_COLORS}
        groupKey="by_grade"
        groupField="content_areas"
      />
      {PROVINCE_ORDER.map(pid => (
        <ProvinceCurriculumSection
          key={pid}
          provinceId={pid}
          data={provinces?.[pid]}
        />
      ))}
    </>
  );
}

// --- CEFR Tab Components ---

function CEFRTopicRow({ topic, color }) {
  const { t } = useLanguage();
  const gamesStr = topic.games && topic.games.length > 0
    ? topic.games.map(g => g.name).join(', ')
    : '';
  const catTotal = topic.catalogue_total || 0;
  const depthPct = catTotal > 0 ? Math.round((topic.item_count / catTotal) * 100) : 0;
  const isTouched = topic.touched !== false && topic.item_count > 0;

  return (
    <div data-testid={`cefr-topic-${topic.topic}`} style={{
      display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
      padding: '0.625rem 1rem', borderRadius: '0.5rem',
      background: isTouched ? `${color}08` : '#FAFBFC',
      border: `1.5px solid ${isTouched ? `${color}20` : '#F1F5F9'}`,
    }}>
      <div style={{
        width: 22, height: 22, borderRadius: '50%', flexShrink: 0, marginTop: 1,
        background: isTouched ? color : '#E2E8F0',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {isTouched && <Check size={11} color="white" strokeWidth={3} />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.375rem', flexWrap: 'wrap' }}>
          <span style={{
            fontFamily: 'Nunito, sans-serif', fontSize: '0.875rem', fontWeight: 700,
            color: isTouched ? '#1A1D20' : '#94A3B8',
          }}>
            {t(`topic.${topic.topic}`) !== `topic.${topic.topic}` ? t(`topic.${topic.topic}`) : topic.label}
          </span>
          {isTouched && catTotal > 0 && (
            <DepthBadge itemCount={topic.item_count} catalogueTotal={catTotal} />
          )}
        </div>
        {isTouched ? (
          <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.775rem', color: '#64748B', marginTop: '0.2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.15rem' }}>
              <span style={{ fontWeight: 600, color: '#1A1D20' }}>
                {topic.item_count} {t('dash.of')} {catTotal} {t('page.items')}
              </span>
              {catTotal > 0 && (
                <>
                  <div style={{ flex: '0 0 60px', height: 4, background: '#F1F5F9', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', borderRadius: 2,
                      width: `${Math.min(depthPct, 100)}%`,
                      background: depthPct >= 25 ? '#059669' : depthPct >= 5 ? '#EAB308' : '#EF4444',
                    }} />
                  </div>
                  <span style={{ fontSize: '0.7rem', color: '#94A3B8' }}>{depthPct}%</span>
                </>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', alignItems: 'center' }}>
              <span>{topic.session_count} {topic.session_count !== 1 ? t('dash.sessions') : t('dash.session')}</span>
              {gamesStr && <>
                <span style={{ color: '#CBD5E1' }}>&middot;</span>
                <span>{gamesStr}</span>
              </>}
            </div>
          </div>
        ) : (
          <div style={{
            fontFamily: 'Nunito, sans-serif', fontSize: '0.775rem', color: '#94A3B8',
            marginTop: '0.2rem', fontStyle: 'italic',
          }}>
            {t('prog.notYetExplored')} &middot; {catTotal > 0 ? `${catTotal} ${t('prog.itemsAvailable')}` : t('prog.noItemsAvailable')}
          </div>
        )}
      </div>
    </div>
  );
}

function CEFRLevelCard({ cefr, data, color }) {
  const [expanded, setExpanded] = useState(false);
  const { t } = useLanguage();
  const depthPct = data.catalogue_total > 0 ? Math.round((data.item_count / data.catalogue_total) * 100) : 0;

  const CEFR_LABEL_MAP = { 'PRE-A1': 'cefr.beginner', 'A1': 'cefr.elementary', 'A1-A2': 'cefr.intermediate', 'A2': 'cefr.upperIntermediate' };
  const translatedLabel = CEFR_LABEL_MAP[cefr] ? `${t(CEFR_LABEL_MAP[cefr])} (${cefr})` : data.label;

  return (
    <div data-testid={`cefr-level-${cefr}`} style={{
      background: 'white', borderRadius: '0.875rem', border: '1px solid #E2E8F0',
      marginBottom: '0.75rem', overflow: 'hidden',
    }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: '1rem 1.25rem', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: expanded ? '#FAFBFC' : 'white',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
          <div style={{
            width: 40, height: 40, borderRadius: '0.5rem',
            background: data.touched ? color : '#F1F5F9',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <span style={{
              fontFamily: 'Fredoka, sans-serif', fontSize: '0.75rem', fontWeight: 600,
              color: data.touched ? 'white' : '#94A3B8',
            }}>
              {cefr}
            </span>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.925rem', fontWeight: 700, color: data.touched ? '#1A1D20' : '#94A3B8' }}>
              {translatedLabel}
            </div>
            {data.touched ? (
              <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', color: '#64748B', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <span>{data.topics_touched} {t('dash.of')} {data.topics_total} {t('prog.topicsLabel')}</span>
                <span style={{ color: '#CBD5E1' }}>&middot;</span>
                <span>{data.item_count} {t('prog.itemsEncountered')}</span>
                {data.catalogue_total > 0 && <>
                  <div style={{ flex: '0 0 50px', height: 4, background: '#F1F5F9', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', borderRadius: 2, width: `${Math.min(depthPct, 100)}%`,
                      background: depthPct >= 25 ? '#059669' : depthPct >= 5 ? '#EAB308' : '#EF4444',
                    }} />
                  </div>
                  <span style={{ fontSize: '0.7rem', color: '#94A3B8' }}>{depthPct}%</span>
                </>}
                <span style={{ color: '#CBD5E1' }}>&middot;</span>
                <span>{data.session_count} {data.session_count !== 1 ? t('dash.sessions') : t('dash.session')}</span>
              </div>
            ) : (
              <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', color: '#94A3B8', fontStyle: 'italic' }}>
                {t('prog.notYetExplored')} &middot; {data.topics_total} {t('prog.topicsAvailable')}
              </div>
            )}
          </div>
        </div>
        {data.touched && (
          <ChevronDown size={16} color="#94A3B8" style={{
            transition: 'transform 0.2s',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0)',
          }} />
        )}
      </div>

      {expanded && data.touched && data.topics.length > 0 && (
        <div style={{ padding: '0.5rem 1.25rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {/* Difficulty distribution */}
          {Object.keys(data.difficulties || {}).length > 0 && (
            <div style={{
              display: 'flex', gap: '0.5rem', marginBottom: '0.375rem', flexWrap: 'wrap',
            }}>
              {['introductory', 'practice', 'mastery'].map(d => {
                const count = (data.difficulties || {})[d] || 0;
                if (!count) return null;
                const colors = { introductory: '#2F9E44', practice: '#4C6EF5', mastery: '#E03131' };
                return (
                  <span key={d} style={{
                    padding: '0.2rem 0.625rem', borderRadius: '0.25rem',
                    background: `${colors[d]}10`, border: `1px solid ${colors[d]}30`,
                    fontFamily: 'Nunito, sans-serif', fontSize: '0.725rem', fontWeight: 600,
                    color: colors[d],
                  }}>
                    {t(`diff.${d}`)}: {count} {t('page.items')}
                  </span>
                );
              })}
            </div>
          )}
          {/* Topics */}
          {data.topics.map(t => (
            <CEFRTopicRow key={t.topic} topic={t} color={color} />
          ))}
          {/* Games used */}
          {data.games && data.games.length > 0 && (
            <div style={{
              fontFamily: 'Nunito, sans-serif', fontSize: '0.775rem', color: '#94A3B8',
              marginTop: '0.25rem',
            }}>
              {t('prog.gamesUsed')}: {data.games.map(g => g.name).join(', ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CEFRTab({ cefrData }) {
  const { t } = useLanguage();
  if (!cefrData) return null;

  return (
    <div data-testid="cefr-progress-section" style={{ marginBottom: '2.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
        <GraduationCap size={20} color="#1E2140" />
        <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.35rem', fontWeight: 700, color: '#1A1D20', margin: 0 }}>
          {t('prog.cefrLevelProgress')}
        </h2>
      </div>

      {/* Summary card */}
      <div style={{
        background: 'white', borderRadius: '0.875rem', border: '1px solid #E2E8F0',
        padding: '1.25rem 1.5rem', marginBottom: '1.25rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.875rem' }}>
          <div>
            <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.9rem', color: '#1A1D20' }}>
              {t('prog.yourSessionsInclude')}{' '}
              <strong style={{ fontFamily: 'Fredoka, sans-serif', fontSize: '1.5rem' }}>{cefrData.levels_touched}</strong>
              {' '}{t('dash.of')} {cefrData.levels_total} {t('prog.cefrLevelsLabel')}
              {cefrData.topics_touched > 0 && (
                <span style={{ color: '#64748B' }}>
                  {' '}{t('dash.across')} <strong style={{ fontFamily: 'Fredoka, sans-serif', fontSize: '1.1rem', color: '#1A1D20' }}>{cefrData.topics_touched}</strong> {t('prog.topicsLabel')}
                </span>
              )}
            </span>
          </div>
          {cefrData.session_count > 0 && (
            <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', color: '#94A3B8' }}>
              {t('dash.across')} {cefrData.session_count} {cefrData.session_count !== 1 ? t('dash.sessions') : t('dash.session')}
              {cefrData.unique_games > 0 && <> &middot; {cefrData.unique_games} {cefrData.unique_games !== 1 ? t('dash.games') : t('dash.game')}</>}
            </span>
          )}
        </div>

        {/* Level dot grid */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '0.875rem' }}>
          {Object.entries(cefrData.by_level || {}).map(([cefr, data]) => (
            <div
              key={cefr}
              title={`${data.label}${data.touched ? ' (active)' : ''}`}
              style={{
                flex: 1, height: 8, borderRadius: 4,
                background: data.touched ? (CEFR_COLORS[cefr] || '#94A3B8') : '#F1F5F9',
                border: data.touched ? 'none' : '1px solid #E2E8F0',
              }}
            />
          ))}
        </div>

        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {Object.entries(cefrData.by_level || {}).map(([cefr, data]) => (
            <div key={cefr} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: CEFR_COLORS[cefr] || '#94A3B8' }} />
              <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.825rem', fontWeight: 600, color: '#1A1D20' }}>{cefr}</span>
              <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.825rem', color: '#94A3B8' }}>
                {data.topics_touched}/{data.topics_total}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Level cards */}
      {Object.entries(cefrData.by_level || {}).map(([cefr, data]) => (
        <CEFRLevelCard key={cefr} cefr={cefr} data={data} color={CEFR_COLORS[cefr] || '#94A3B8'} />
      ))}

      <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.775rem', color: '#94A3B8', marginTop: '0.75rem', fontStyle: 'italic', lineHeight: 1.5 }}>
        {t('prog.cefrDepthNote')}
      </p>
    </div>
  );
}

// --- Download Report Modal ---

// Single-select shortcuts: pick ALL curricula, or pick just CEFR (CEFR is not a province).
const QUICK_PRESETS = [
  { key: 'all',  labelKey: 'prog.selectAll',  descKey: 'prog.selectAllDesc' },
  { key: 'none', labelKey: 'prog.clearAll',   descKey: 'prog.clearAllDesc' },
];

// Curricula the user can multi-select. CEFR is a useful pedagogical lens that lives
// alongside the provincial curricula. Ontario + BC kept at top for B2B primacy.
const CURRICULUM_OPTIONS = [
  { key: 'cefr',          labelKey: 'prog.cefrLevelProgress', descKey: 'prog.cefrDesc' },
  { key: 'ontario',       labelKey: 'prog.ontarioCurriculum', descKey: 'prog.provincialDesc' },
  { key: 'bc',            labelKey: 'prog.bcCurriculum',      descKey: 'prog.provincialDesc' },
];

const OTHER_PROVINCE_REPORTS = [
  { key: 'saskatchewan',  labelKey: 'prog.saskatchewanCurriculum' },
  { key: 'manitoba',      labelKey: 'prog.manitobaCurriculum' },
  { key: 'alberta',       labelKey: 'prog.albertaCurriculum' },
  { key: 'newfoundland',  labelKey: 'prog.newfoundlandCurriculum' },
  { key: 'nova_scotia',   labelKey: 'prog.novaScotiaCurriculum' },
  { key: 'new_brunswick', labelKey: 'prog.newBrunswickCurriculum' },
  { key: 'pei',           labelKey: 'prog.peiCurriculum' },
];

const ALL_SELECTABLE_KEYS = [
  ...CURRICULUM_OPTIONS.map(o => o.key),
  ...OTHER_PROVINCE_REPORTS.map(o => o.key),
];

const PERIOD_KEYS = [
  { key: 'today', labelKey: 'prog.today' },
  { key: 'weekly', labelKey: 'prog.last7Days' },
  { key: 'monthly', labelKey: 'prog.last30Days' },
  { key: 'all', labelKey: 'prog.allTime' },
  { key: 'custom', labelKey: 'prog.customRange' },
];

function DownloadReportModal({ isOpen, onClose, classroomId }) {
  // Default: Ontario + BC (the two most-purchased markets). User can toggle freely.
  const [selected, setSelected] = useState(new Set(['ontario', 'bc']));
  const [period, setPeriod] = useState('all');
  const { t } = useLanguage();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const toggle = (key) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(ALL_SELECTABLE_KEYS));
  const clearAll = () => setSelected(new Set());

  const handleDownload = async () => {
    setError('');
    if (selected.size === 0) {
      setError(t('prog.selectAtLeastOne'));
      return;
    }
    if (period === 'custom' && (!startDate || !endDate)) {
      setError(t('prog.selectBothDates'));
      return;
    }
    setDownloading(true);
    try {
      // Send curricula in display order (Ontario+BC first, then others) so the PDF reads naturally.
      const orderedKeys = ALL_SELECTABLE_KEYS.filter(k => selected.has(k));
      let url = `${API_URL}/teacher/progress-report/pdf?report_types=${orderedKeys.join(',')}&period=${period}`;
      if (period === 'custom') {
        url += `&start_date=${startDate}&end_date=${endDate}`;
      }
      if (classroomId) {
        url += `&classroom_id=${classroomId}`;
      }
      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const slug = orderedKeys.length > 1 ? 'multi' : orderedKeys[0];
      link.download = `progress_report_${slug}_${period}_${new Date().toISOString().slice(0, 10)}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
      onClose();
    } catch (err) {
      setError(t('prog.failedGenerate'));
    }
    setDownloading(false);
  };

  return (
    <div data-testid="download-report-modal" style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} />
      <div style={{
        position: 'relative', background: 'white', borderRadius: '1rem',
        padding: '2rem', width: '100%', maxWidth: 460,
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
      }}>
        <button onClick={onClose} data-testid="close-report-modal" style={{
          position: 'absolute', top: '1rem', right: '1rem',
          background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8',
        }}>
          <X size={20} />
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <Download size={20} color="#1E2140" />
          <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.25rem', fontWeight: 700, color: '#1A1D20', margin: 0 }}>
            {t('prog.downloadProgressReport')}
          </h2>
        </div>
        <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.78rem', color: '#64748B', marginBottom: '1.25rem' }}>
          {t('prog.multiSelectHint')}
        </div>

        {/* Curricula — multi-select checkboxes */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <label style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', fontWeight: 700, color: '#1A1D20' }}>
            {t('prog.curricula')} <span style={{ color: '#94A3B8', fontWeight: 500 }}>({selected.size})</span>
          </label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              data-testid="curricula-select-all"
              onClick={selectAll}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#1E2140', fontSize: '0.75rem', fontWeight: 700, textDecoration: 'underline', fontFamily: 'Nunito, sans-serif' }}
            >
              {t('prog.selectAll')}
            </button>
            <span style={{ color: '#CBD5E1' }}>·</span>
            <button
              data-testid="curricula-clear-all"
              onClick={clearAll}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#94A3B8', fontSize: '0.75rem', fontWeight: 700, textDecoration: 'underline', fontFamily: 'Nunito, sans-serif' }}
            >
              {t('prog.clearAll')}
            </button>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}>
          {CURRICULUM_OPTIONS.map(rt => {
            const isChecked = selected.has(rt.key);
            return (
              <button
                key={rt.key}
                data-testid={`report-type-${rt.key}`}
                onClick={() => toggle(rt.key)}
                role="checkbox"
                aria-checked={isChecked}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '0.7rem 1rem', borderRadius: '0.625rem',
                  border: isChecked ? '2px solid #1E2140' : '1.5px solid #E2E8F0',
                  background: isChecked ? '#F8F9FB' : 'white',
                  cursor: 'pointer', textAlign: 'left',
                }}
              >
                <div style={{
                  width: 18, height: 18, borderRadius: '0.3rem',
                  border: '2px solid ' + (isChecked ? '#1E2140' : '#CBD5E1'),
                  background: isChecked ? '#1E2140' : 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxSizing: 'border-box', flexShrink: 0,
                }}>
                  {isChecked && <Check size={12} color="white" strokeWidth={3} />}
                </div>
                <div>
                  <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.875rem', fontWeight: 700, color: '#1A1D20' }}>
                    {t(rt.labelKey)}
                  </div>
                  <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.75rem', color: '#94A3B8' }}>
                    {t(rt.descKey)}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Other provinces — checkboxes as pills */}
        <div style={{
          fontFamily: 'Nunito, sans-serif', fontSize: '0.75rem', fontWeight: 700,
          color: '#64748B', letterSpacing: '0.05em', textTransform: 'uppercase',
          marginBottom: '0.5rem',
        }}>
          {t('prog.otherProvinces')}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '1.25rem' }}>
          {OTHER_PROVINCE_REPORTS.map(rt => {
            const isActive = selected.has(rt.key);
            return (
              <button
                key={rt.key}
                data-testid={`report-type-${rt.key}`}
                onClick={() => toggle(rt.key)}
                role="checkbox"
                aria-checked={isActive}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                  padding: '0.4rem 0.85rem', borderRadius: '2rem',
                  border: isActive ? '2px solid #1E2140' : '1.5px solid #E2E8F0',
                  background: isActive ? '#1E2140' : 'white',
                  color: isActive ? 'white' : '#64748B',
                  cursor: 'pointer',
                  fontFamily: 'Nunito, sans-serif', fontSize: '0.78rem', fontWeight: 600,
                  transition: 'all 0.15s',
                }}
              >
                {isActive && <Check size={12} strokeWidth={3} />}
                {t(rt.labelKey)}
              </button>
            );
          })}
        </div>

        {/* Period */}
        <label style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', fontWeight: 700, color: '#1A1D20', display: 'block', marginBottom: '0.5rem' }}>
          {t('prog.timePeriod')}
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
          {PERIOD_KEYS.map(p => (
            <button
              key={p.key}
              data-testid={`report-period-${p.key}`}
              onClick={() => setPeriod(p.key)}
              style={{
                padding: '0.5rem 1rem', borderRadius: '2rem',
                border: period === p.key ? '2px solid #1E2140' : '1.5px solid #E2E8F0',
                background: period === p.key ? '#1E2140' : 'white',
                color: period === p.key ? 'white' : '#64748B',
                cursor: 'pointer',
                fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', fontWeight: 600,
              }}
            >
              {t(p.labelKey)}
            </button>
          ))}
        </div>

        {/* Custom date range */}
        {period === 'custom' && (
          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.75rem', fontWeight: 600, color: '#64748B', display: 'block', marginBottom: '0.25rem' }}>
                {t('prog.startDate')}
              </label>
              <input
                type="date"
                data-testid="report-start-date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                style={{
                  width: '100%', padding: '0.5rem 0.75rem', borderRadius: '0.5rem',
                  border: '1.5px solid #E2E8F0', fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem',
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.75rem', fontWeight: 600, color: '#64748B', display: 'block', marginBottom: '0.25rem' }}>
                {t('prog.endDate')}
              </label>
              <input
                type="date"
                data-testid="report-end-date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                style={{
                  width: '100%', padding: '0.5rem 0.75rem', borderRadius: '0.5rem',
                  border: '1.5px solid #E2E8F0', fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem',
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>
          </div>
        )}

        {error && (
          <div data-testid="report-error" style={{
            fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', color: '#DC2626',
            marginBottom: '0.75rem',
          }}>
            {error}
          </div>
        )}

        <button
          data-testid="download-report-btn"
          onClick={handleDownload}
          disabled={downloading || selected.size === 0}
          style={{
            width: '100%', padding: '0.875rem',
            borderRadius: '0.75rem', border: 'none',
            background: (downloading || selected.size === 0) ? '#94A3B8' : '#1E2140',
            color: 'white', cursor: (downloading || selected.size === 0) ? 'not-allowed' : 'pointer',
            fontFamily: 'Outfit, sans-serif', fontSize: '0.95rem', fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
            transition: 'background 0.2s',
          }}
        >
          {downloading ? (
            <><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> {t('prog.generating')}</>
          ) : (
            <><Download size={18} /> {selected.size > 1 ? `${t('prog.downloadPdf')} (${selected.size})` : t('prog.downloadPdf')}</>
          )}
        </button>
      </div>
    </div>
  );
}

// --- Main Page ---

const TABS = [
  { key: 'cefr', labelKey: 'prog.byCefrLevel', icon: GraduationCap },
  { key: 'curriculum', labelKey: 'prog.byCurriculum', icon: BookOpen },
];

export function Curriculum() {
  const { user } = useAuth();
  const { activeClassroom } = useClassroom();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('cefr');
  const [ontario, setOntario] = useState(null);
  const [bc, setBc] = useState(null);
  const [provinces, setProvinces] = useState({});  // { provinceId: data }
  const [cefrData, setCefrData] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);

  useEffect(() => {
    if (user?.demo) return;
    const cid = activeClassroom?.id;
    const q = cid ? `?classroom_id=${cid}` : '';
    axios.get(`${API_URL}/teacher/curriculum-coverage${q}`).then(r => setOntario(r.data)).catch(() => {});
    axios.get(`${API_URL}/teacher/curriculum-coverage-bc${q}`).then(r => setBc(r.data)).catch(() => {});
    axios.get(`${API_URL}/teacher/progress-cefr${q}`).then(r => setCefrData(r.data)).catch(() => {});

    // 7 additional provinces — fire in parallel; render each as data arrives
    PROVINCE_ORDER.forEach(pid => {
      axios.get(`${API_URL}/teacher/curriculum-coverage/${pid}${q}`)
        .then(r => setProvinces(prev => ({ ...prev, [pid]: r.data })))
        .catch(() => {});
    });
  }, [user, activeClassroom]);

  return (
    <div data-testid="progress-page" style={{ height: '100%', overflow: 'auto', padding: '2rem 2.5rem', background: '#F8F9FB' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.75rem', fontWeight: 700, color: '#1A1D20', margin: 0 }}>
            {t('prog.title')}
          </h1>
          <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', color: '#64748B', margin: '0.25rem 0 0' }}>
            {t('prog.subtitle')}
          </p>
        </div>
        {!user?.demo && (
          <button
            data-testid="open-download-report"
            onClick={() => setShowReportModal(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.625rem 1.25rem', borderRadius: '0.75rem',
              background: 'white', border: '1.5px solid #E2E8F0',
              color: '#1A1D20', cursor: 'pointer',
              fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', fontWeight: 600,
              transition: 'all 0.15s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#1E2140'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(30,33,64,0.12)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)'; }}
          >
            <Download size={16} />
            {t('prog.downloadReport')}
          </button>
        )}
      </div>

      <DownloadReportModal isOpen={showReportModal} onClose={() => setShowReportModal(false)} classroomId={activeClassroom?.id} />

      {user?.demo && (
        <div style={{
          padding: '1.5rem', background: 'white', borderRadius: '0.875rem',
          border: '1px solid #E2E8F0', textAlign: 'center',
        }}>
          <p style={{ fontFamily: 'Nunito, sans-serif', color: '#64748B', margin: 0 }}>
            {t('prog.demoMessage')}
          </p>
        </div>
      )}

      {!user?.demo && (
        <>
          {/* Tab switcher — same style as Browse page */}
          <div data-testid="progress-tab-switcher" style={{
            display: 'inline-flex', borderRadius: '2rem', border: '2px solid #E2E8F0',
            overflow: 'hidden', marginBottom: '1.5rem', background: 'white',
          }}>
            {TABS.map(tab => {
              const isActive = activeTab === tab.key;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  data-testid={`progress-tab-${tab.key}`}
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.625rem 1.5rem',
                    background: isActive ? '#1E2140' : 'transparent',
                    color: isActive ? 'white' : '#64748B',
                    border: 'none', cursor: 'pointer',
                    fontFamily: 'Nunito, sans-serif', fontSize: '0.875rem', fontWeight: 600,
                    transition: 'all 0.2s',
                  }}
                >
                  <Icon size={16} />
                  {t(tab.labelKey)}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          {activeTab === 'cefr' && <CEFRTab cefrData={cefrData} />}
          {activeTab === 'curriculum' && <CurriculumTab ontario={ontario} bc={bc} provinces={provinces} />}
        </>
      )}
    </div>
  );
}
